import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { PlayerSliceState } from '../types';

/**
 * Redux slice for player state management
 */

const initialState: PlayerSliceState = {
  // Stats
  health: 100,
  maxHealth: 100,
  stamina: 100,
  maxStamina: 100,
  speed: 100, // base 100%
  level: 1,
  xp: 0,
  xpToNextLevel: 100,

  // Position
  position: { x: 0, y: 0 },
  velocity: { x: 0, y: 0 },

  // Equipment
  flashlightBattery: 100,
  hasMask: true,
  isUsingMask: false,
  isUsingFlashlight: false,

  // State
  isAlive: true,
  isHiding: false,
  isCrouching: false,
  isSprinting: false,
};

const playerSlice = createSlice({
  name: 'player',
  initialState,
  reducers: {
    // Health management
    setHealth: (state, action: PayloadAction<number>) => {
      state.health = Math.max(0, Math.min(action.payload, state.maxHealth));
      if (state.health === 0) {
        state.isAlive = false;
      }
    },

    takeDamage: (state, action: PayloadAction<number>) => {
      state.health = Math.max(0, state.health - action.payload);
      if (state.health === 0) {
        state.isAlive = false;
      }
    },

    heal: (state, action: PayloadAction<number>) => {
      state.health = Math.min(state.maxHealth, state.health + action.payload);
    },

    setMaxHealth: (state, action: PayloadAction<number>) => {
      state.maxHealth = action.payload;
      state.health = Math.min(state.health, state.maxHealth);
    },

    // Stamina management
    setStamina: (state, action: PayloadAction<number>) => {
      state.stamina = Math.max(0, Math.min(action.payload, state.maxStamina));
    },

    drainStamina: (state, action: PayloadAction<number>) => {
      state.stamina = Math.max(0, state.stamina - action.payload);
    },

    restoreStamina: (state, action: PayloadAction<number>) => {
      state.stamina = Math.min(state.maxStamina, state.stamina + action.payload);
    },

    setMaxStamina: (state, action: PayloadAction<number>) => {
      state.maxStamina = action.payload;
      state.stamina = Math.min(state.stamina, state.maxStamina);
    },

    // Speed
    setSpeed: (state, action: PayloadAction<number>) => {
      state.speed = action.payload;
    },

    // XP and leveling
    addXP: (state, action: PayloadAction<number>) => {
      state.xp += action.payload;
      while (state.xp >= state.xpToNextLevel) {
        state.xp -= state.xpToNextLevel;
        state.level += 1;
        // XP requirement increases each level
        state.xpToNextLevel = Math.floor(state.xpToNextLevel * 1.5);
      }
    },

    setLevel: (state, action: PayloadAction<number>) => {
      state.level = action.payload;
    },

    // Position and movement
    setPosition: (state, action: PayloadAction<{ x: number; y: number }>) => {
      state.position = action.payload;
    },

    setVelocity: (state, action: PayloadAction<{ x: number; y: number }>) => {
      state.velocity = action.payload;
    },

    // Equipment
    setFlashlightBattery: (state, action: PayloadAction<number>) => {
      state.flashlightBattery = Math.max(0, Math.min(100, action.payload));
    },

    drainFlashlightBattery: (state, action: PayloadAction<number>) => {
      state.flashlightBattery = Math.max(0, state.flashlightBattery - action.payload);
      if (state.flashlightBattery === 0) {
        state.isUsingFlashlight = false;
      }
    },

    rechargeFlashlight: (state, action: PayloadAction<number>) => {
      state.flashlightBattery = Math.min(100, state.flashlightBattery + action.payload);
    },

    toggleFlashlight: (state) => {
      if (state.flashlightBattery > 0) {
        state.isUsingFlashlight = !state.isUsingFlashlight;
      } else {
        state.isUsingFlashlight = false;
      }
    },

    setFlashlight: (state, action: PayloadAction<boolean>) => {
      state.isUsingFlashlight = action.payload && state.flashlightBattery > 0;
    },

    toggleMask: (state) => {
      state.isUsingMask = !state.isUsingMask;
    },

    setMask: (state, action: PayloadAction<boolean>) => {
      state.isUsingMask = action.payload && state.hasMask;
    },

    giveMask: (state) => {
      state.hasMask = true;
    },

    // Player state
    setAlive: (state, action: PayloadAction<boolean>) => {
      state.isAlive = action.payload;
    },

    setHiding: (state, action: PayloadAction<boolean>) => {
      state.isHiding = action.payload;
    },

    setCrouching: (state, action: PayloadAction<boolean>) => {
      state.isCrouching = action.payload;
    },

    setSprinting: (state, action: PayloadAction<boolean>) => {
      state.isSprinting = action.payload;
    },

    // Reset player
    resetPlayer: () => initialState,

    // Load player state from save
    loadPlayerState: (state, action: PayloadAction<Partial<PlayerSliceState>>) => {
      return { ...state, ...action.payload };
    },
  },
});

export const {
  setHealth,
  takeDamage,
  heal,
  setMaxHealth,
  setStamina,
  drainStamina,
  restoreStamina,
  setMaxStamina,
  setSpeed,
  addXP,
  setLevel,
  setPosition,
  setVelocity,
  setFlashlightBattery,
  drainFlashlightBattery,
  rechargeFlashlight,
  toggleFlashlight,
  setFlashlight,
  toggleMask,
  setMask,
  giveMask,
  setAlive,
  setHiding,
  setCrouching,
  setSprinting,
  resetPlayer,
  loadPlayerState,
} = playerSlice.actions;

export default playerSlice.reducer;
